import type { ResolvedTheme, ThemeSetting } from './themeTypes';

export const THEME_STORAGE_KEY = 'kudu-theme-setting';

export const THEME_SETTINGS: readonly ThemeSetting[] = [
  'light',
  'dark',
  'system',
];

export function isThemeSetting(value: unknown): value is ThemeSetting {
  return (
    typeof value === 'string' &&
    (THEME_SETTINGS as readonly string[]).includes(value)
  );
}

export function isAppRoute(pathname: string): boolean {
  return pathname === '/app' || pathname.startsWith('/app/');
}

export function getSystemResolvedTheme(): ResolvedTheme {
  if (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function'
  ) {
    return window.matchMedia('(prefers-color-scheme: dark)')?.matches
      ? 'dark'
      : 'light';
  }

  return 'light';
}

export function resolveTheme(
  setting: ThemeSetting,
  systemTheme: ResolvedTheme = getSystemResolvedTheme(),
): ResolvedTheme {
  return setting === 'system' ? systemTheme : setting;
}

export function readStoredThemeSetting(
  storage: Storage | null | undefined = typeof window !== 'undefined'
    ? window.localStorage
    : null,
): ThemeSetting | null {
  try {
    const storedValue = storage?.getItem(THEME_STORAGE_KEY);
    return isThemeSetting(storedValue) ? storedValue : null;
  } catch {
    return null;
  }
}

export function writeStoredThemeSetting(
  setting: ThemeSetting,
  storage: Storage | null | undefined = typeof window !== 'undefined'
    ? window.localStorage
    : null,
) {
  try {
    storage?.setItem(THEME_STORAGE_KEY, setting);
  } catch {
    // Storage may be unavailable in private browsing or restricted contexts.
  }
}

export function applyThemeToDocument(theme: ResolvedTheme) {
  if (typeof document === 'undefined') {
    return;
  }

  document.documentElement.setAttribute('data-theme', theme);
}

export function getInitialThemeSetting(): ThemeSetting {
  return readStoredThemeSetting() ?? 'light';
}

export function getInitialResolvedTheme(
  pathname: string = typeof window !== 'undefined'
    ? window.location.pathname
    : '/app',
): ResolvedTheme {
  if (!isAppRoute(pathname)) {
    return 'light';
  }

  return resolveTheme(getInitialThemeSetting());
}

export function applyInitialTheme() {
  applyThemeToDocument(getInitialResolvedTheme());
}

export const themeBootstrapScript = `(function(){try{var key='${THEME_STORAGE_KEY}';var path=window.location&&window.location.pathname?window.location.pathname:'';var isApp=path==='/app'||path.indexOf('/app/')===0;var setting=localStorage.getItem(key);var valid=setting==='light'||setting==='dark'||setting==='system';var theme='light';if(isApp&&valid){if(setting==='dark'){theme='dark';}else if(setting==='system'){theme=window.matchMedia&&window.matchMedia('(prefers-color-scheme: dark)').matches?'dark':'light';}}document.documentElement.setAttribute('data-theme',theme);}catch(e){document.documentElement.setAttribute('data-theme','light');}})();`;
